
import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useMicrocuencasCRUD } from '../hooks/useMicrocuencasCRUD';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ScatterChart, Scatter, Area, AreaChart } from
'recharts';
import {Filter, Download, TrendingUp, BarChart3, Activity, MapPin, Droplets, Building2, Home, FileText, Info, X, Globe, FilePdf, Moon, Sun} from 'lucide-react';
import { groupBy, orderBy, sumBy, meanBy, maxBy, minBy } from 'lodash';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

interface AnalysisData {
  name: string;
  [key: string]: string | number;
}

const InteractiveAnalysis = () => {
  const navigate = useNavigate();
  const { microcuencas, loading, fetchMicrocuencas } = useMicrocuencasCRUD();
  const [selectedMunicipality, setSelectedMunicipality] = useState<string>('all');
  const [selectedMicrocuenca, setSelectedMicrocuenca] = useState<string>('all');
  const [chartType, setChartType] = useState<'bar' | 'line' | 'pie' | 'scatter' | 'area'>('bar');
  const [analysisMode, setAnalysisMode] = useState<'municipality' | 'microcuenca' | 'comparison'>('municipality');
  const [selectedMetric, setSelectedMetric] = useState<string>('superficie_agricola');
  const [showIndicatorsModal, setShowIndicatorsModal] = useState(false);
  const [darkMode, setDarkMode] = useState(true);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#FF7300'];

  const metrics = [
   { key: 'sup_micro', label: 'Superficie Microcuenca', unit: 'ha' },
  { key: 'superficie_agricola', label: 'Superficie Agrícola', unit: 'ha' },
  { key: 'superficie_pasto', label: 'Superficie Pasto', unit: 'ha' },
  { key: 'superficie_forestal', label: 'Superficie Forestal', unit: 'ha' },
  { key: 'superficie_incendiada', label: 'Superficie Incendiada', unit: 'ha' },
  { key: 'superficie_deforestada', label: 'Superficie Deforestada', unit: 'ha' },
  { key: 'superficie_anp', label: 'Superficie ANP', unit: 'ha' },
  { key: 'densidad_poblacion', label: 'Densidad Población', unit: 'hab/km²' },
  { key: 'indice_', label: 'Densidad Población', unit: 'hab/km²' },

  { key: 'precipitacion', label: 'Precipitación', unit: 'mm' },
  { key: 'temperatura', label: 'Temperatura', unit: '°C' },
  { key: 'altitud', label: 'Altitud', unit: 'msnm' },
  { key: 'pendiente', label: 'Pendiente', unit: '%' }];


  const indicadoresPriorizacion = [
  { indicador: 'Fracción de escurrimiento', ponderacion: '0.2475 (24.75%)', campo: 'pond_escur' },
  { indicador: 'Erosión', ponderacion: '0.2267 (22.67%)', campo: 'eros_erosi' },
  { indicador: 'Tasa de deforestación', ponderacion: '0.1292 (12.92%)', campo: 'SUP_DEFORE' },
  { indicador: 'Uso agropecuario (Suma de Sup_Agrico + Sup_pasto_ + Sup_forest)', ponderacion: '0.1292 (12.92%)', campo: 'Uso_agropecuario (calculado)' },
  { indicador: 'Incendios', ponderacion: '0.0867 (8.67%)', campo: 'Sup_incend' },
  { indicador: 'Importancia para la conservación', ponderacion: '0.0608 (6.08%)', campo: 'Importancia_conservacion' },
  { indicador: 'Densidad de población', ponderacion: '0.0579 (5.79%)', campo: 'Densidad_poblacion' },
  { indicador: 'Rezago social', ponderacion: '0.0621 (6.21%)', campo: 'Rezago_social' },
  { indicador: 'ÍNDICE DE PRIORIDAD TOTAL', ponderacion: '1.0000 (100%)', campo: 'Indice_Prioridad' }];


  useEffect(() => {
    fetchMicrocuencas();
  }, [fetchMicrocuencas]);

  const municipalities = useMemo(() => {
    return [...new Set(microcuencas.map((m) => m.municipio))].sort();
  }, [microcuencas]);

  const microcuencasNames = useMemo(() => {
    return [...new Set(microcuencas.map((m) => m.microcuenca))].sort();
  }, [microcuencas]);

  const filteredData = useMemo(() => {
    return microcuencas.filter((m) => {
      const municipalityMatch = selectedMunicipality === 'all' || m.municipio === selectedMunicipality;
      const microcuencaMatch = selectedMicrocuenca === 'all' || m.microcuenca === selectedMicrocuenca;
      return municipalityMatch && microcuencaMatch;
    });
  }, [microcuencas, selectedMunicipality, selectedMicrocuenca]);

  const chartData = useMemo(() => {
    if (analysisMode === 'municipality') {
      const grouped = groupBy(filteredData, 'municipio');
      return Object.entries(grouped).map(([municipality, items]) => ({
        name: municipality,
        superficie_agricola: sumBy(items, 'superficie_agricola'),
        sup_micro: sumBy(items, 'sup_micro'),
        superficie_pasto: sumBy(items, 'superficie_pasto'),
        superficie_forestal: sumBy(items, 'superficie_forestal'),
        densidad_poblacion: meanBy(items, 'densidad_poblacion'),
        precipitacion: meanBy(items, 'precipitacion'),
        temperatura: meanBy(items, 'temperatura'),
        altitud: meanBy(items, 'altitud'),
        pendiente: meanBy(items, 'pendiente'),
        count: items.length
      }));
    } else if (analysisMode === 'microcuenca') {
      return filteredData.map((item) => ({
        name: item.microcuenca,
        superficie_agricola: item.superficie_agricola,
        sup_micro: item.sup_micro,
        superficie_pasto: item.superficie_pasto,
        superficie_forestal: item.superficie_forestal,
        densidad_poblacion: item.densidad_poblacion,
        precipitacion: item.precipitacion,
        temperatura: item.temperatura,
        altitud: item.altitud,
        pendiente: item.pendiente
      }));
    } else {
      // Comparison mode
      const municipalityData = groupBy(filteredData, 'municipio');
      const microcuencaData = groupBy(filteredData, 'microcuenca');

      return [
      {
        category: 'Municipios',
        count: Object.keys(municipalityData).length,
        avgValue: meanBy(filteredData, selectedMetric as any) || 0
      },
      {
        category: 'Microcuencas',
        count: Object.keys(microcuencaData).length,
        avgValue: meanBy(filteredData, selectedMetric as any) || 0
      }];

    }
  }, [filteredData, analysisMode, selectedMetric]);

  // Nuevo: Datos del cuadro de resumen
  const summaryData = useMemo(() => {
    const selectedMetricData = metrics.find((m) => m.key === selectedMetric);
    const metricValues = filteredData.map((item) => item[selectedMetric as keyof typeof item] as number).filter((val) => !isNaN(val));

    const totalSuperficie = sumBy(filteredData, 'sup_micro');
    const totalAgricola = sumBy(filteredData, 'superficie_agricola');
    const totalPasto = sumBy(filteredData, 'superficie_pasto');
    const totalForestal = sumBy(filteredData, 'superficie_forestal');

    const avgMetric = meanBy(filteredData, selectedMetric as any) || 0;
    const maxItem = maxBy(filteredData, selectedMetric as any);
    const minItem = minBy(filteredData, selectedMetric as any);

    return {
      totalRegistros: filteredData.length,
      municipios: new Set(filteredData.map((m) => m.municipio)).size,
      microcuencas: new Set(filteredData.map((m) => m.microcuenca)).size,
      superficieTotal: totalSuperficie,
      distribucionUso: {
        agricola: totalAgricola,
        pasto: totalPasto,
        forestal: totalForestal,
        porcentajes: {
          agricola: totalSuperficie > 0 ? totalAgricola / totalSuperficie * 100 : 0,
          pasto: totalSuperficie > 0 ? totalPasto / totalSuperficie * 100 : 0,
          forestal: totalSuperficie > 0 ? totalForestal / totalSuperficie * 100 : 0
        }
      },
      metricaSeleccionada: {
        nombre: selectedMetricData?.label || selectedMetric,
        unidad: selectedMetricData?.unit || '',
        promedio: avgMetric,
        maximo: {
          valor: maxItem ? maxItem[selectedMetric as keyof typeof maxItem] as number : 0,
          ubicacion: maxItem ? `${maxItem.microcuenca}, ${maxItem.municipio}` : 'N/A'
        },
        minimo: {
          valor: minItem ? minItem[selectedMetric as keyof typeof minItem] as number : 0,
          ubicacion: minItem ? `${minItem.microcuenca}, ${minItem.municipio}` : 'N/A'
        }
      }
    };
  }, [filteredData, selectedMetric, metrics]);

  const renderChart = () => {
    const selectedMetricData = metrics.find((m) => m.key === selectedMetric);

    switch (chartType) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis
                dataKey="name"
                stroke="#9CA3AF"
                angle={-45}
                textAnchor="end"
                height={100} />

              <YAxis stroke="#9CA3AF" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: '1px solid #374151',
                  borderRadius: '8px'
                }}
                formatter={(value, name) => [
                typeof value === 'number' ? value.toFixed(2) : value,
                selectedMetricData?.label || name]
                } />

              <Legend />
              <Bar
                dataKey={selectedMetric}
                fill={COLORS[0]}
                name={selectedMetricData?.label || selectedMetric} />

            </BarChart>
          </ResponsiveContainer>);


      case 'line':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis
                dataKey="name"
                stroke="#9CA3AF"
                angle={-45}
                textAnchor="end"
                height={100} />

              <YAxis stroke="#9CA3AF" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: '1px solid #374151',
                  borderRadius: '8px'
                }} />

              <Legend />
              <Line
                type="monotone"
                dataKey={selectedMetric}
                stroke={COLORS[0]}
                strokeWidth={3}
                name={selectedMetricData?.label || selectedMetric} />

            </LineChart>
          </ResponsiveContainer>);


      case 'pie':
        const pieData = chartData.slice(0, 8).map((item, index) => ({
          name: item.name,
          value: item[selectedMetric] as number,
          fill: COLORS[index % COLORS.length]
        }));

        return (
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={120}
                fill="#8884d8"
                dataKey="value">

                {pieData.map((entry, index) =>
                <Cell key={`cell-${index}`} fill={entry.fill} />
                )}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>);


      case 'area':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis
                dataKey="name"
                stroke="#9CA3AF"
                angle={-45}
                textAnchor="end"
                height={100} />

              <YAxis stroke="#9CA3AF" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: '1px solid #374151',
                  borderRadius: '8px'
                }} />

              <Legend />
              <Area
                type="monotone"
                dataKey={selectedMetric}
                stackId="1"
                stroke={COLORS[0]}
                fill={COLORS[0]}
                fillOpacity={0.6}
                name={selectedMetricData?.label || selectedMetric} />

            </AreaChart>
          </ResponsiveContainer>);


      case 'scatter':
        const scatterData = chartData.map((item) => ({
          x: item['superficie_agricola'] as number,
          y: item[selectedMetric] as number,
          name: item.name
        }));

        return (
          <ResponsiveContainer width="100%" height={400}>
            <ScatterChart data={scatterData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis
                type="number"
                dataKey="x"
                stroke="#9CA3AF"
                name="Superficie Agrícola" />

              <YAxis
                type="number"
                dataKey="y"
                stroke="#9CA3AF"
                name={selectedMetricData?.label || selectedMetric} />

              <Tooltip
                cursor={{ strokeDasharray: '3 3' }}
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: '1px solid #374151',
                  borderRadius: '8px'
                }} />

              <Scatter fill="#0088FE" />
            </ScatterChart>
          </ResponsiveContainer>);


      default:
        return null;
    }
  };

  const statsCards = useMemo(() => {
    const totalRecords = filteredData.length;
    const uniqueMunicipalities = new Set(filteredData.map((m) => m.municipio)).size;
    const uniqueMicrocuencas = new Set(filteredData.map((m) => m.microcuenca)).size;
    const avgMetric = meanBy(filteredData, selectedMetric as any) || 0;
    const selectedMetricData = metrics.find((m) => m.key === selectedMetric);

    return [
    {
      title: 'Total Registros',
      value: totalRecords.toLocaleString(),
      icon: Activity,
      color: 'from-blue-500 to-blue-600'
    },
    {
      title: 'Municipios',
      value: uniqueMunicipalities.toString(),
      icon: Building2,
      color: 'from-green-500 to-green-600'
    },
    {
      title: 'Microcuencas',
      value: uniqueMicrocuencas.toString(),
      icon: Droplets,
      color: 'from-purple-500 to-purple-600'
    },
    {
      title: `Promedio ${selectedMetricData?.label}`,
      value: `${avgMetric.toFixed(2)} ${selectedMetricData?.unit || ''}`,
      icon: TrendingUp,
      color: 'from-orange-500 to-orange-600'
    }];

  }, [filteredData, selectedMetric, metrics]);

  // Nuevas tarjetas con estadísticas generales de Chiapas
  const chiapasStatsCards = [
  {
    title: 'Superficie Total Chiapas',
    value: '5,979,705 Ha',
    icon: Globe,
    color: 'from-indigo-500 to-indigo-600'
  },
  {
    title: 'Superficie Forestal',
    value: '3,274,497 Ha (54.8%)',
    icon: Activity,
    color: 'from-emerald-500 to-emerald-600'
  },
  {
    title: 'Superficie Agrícola',
    value: '1,467,831 Ha (24.5%)',
    icon: Building2,
    color: 'from-yellow-500 to-yellow-600'
  },
  {
    title: 'Superficie de Pasto',
    value: '1,237,377 Ha (20.7%)',
    icon: Droplets,
    color: 'from-lime-500 to-lime-600'
  },
  {
    title: 'Superficie Incendiada',
    value: '176,051 Ha',
    icon: TrendingUp,
    color: 'from-red-500 to-red-600'
  },
  {
    title: 'Superficie Deforestada',
    value: '25,591 Ha',
    icon: Activity,
    color: 'from-orange-500 to-orange-600'
  },
  {
    title: 'Máx. Prioridad',
    value: '0.5134',
    icon: TrendingUp,
    color: 'from-rose-500 to-rose-600'
  },
  {
    title: 'Prom. Prioridad',
    value: '0.2203',
    icon: Activity,
    color: 'from-cyan-500 to-cyan-600'
  }];


  const exportData = () => {
    try {
      const csv = [
      // Header
      ['Nombre', 'Municipio', 'Microcuenca', ...metrics.map((m) => m.label)].join(','),
      // Data rows
      ...filteredData.map((item) => [
      item.microcuenca,
      item.municipio,
      item.microcuenca,
      ...metrics.map((m) => item[m.key as keyof typeof item] || 0)].
      join(','))].
      join('\n');

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analisis_microcuencas_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Datos exportados exitosamente');
    } catch (error) {
      toast.error('Error al exportar los datos');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full" />

      </div>);

  }

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`}>
      {/* Header */}
      <motion.header
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className={`border-b sticky top-0 z-50 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>

        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>

                <Home className={`w-6 h-6 ${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`} />
              </button>
              <BarChart3 className={`w-8 h-8 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
              <div>
                <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Análisis Interactivo</h1>
                <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Microcuencas de Chiapas - Análisis de Datos</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setDarkMode(!darkMode)}
                className={`p-2 rounded-lg transition-colors ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'}`}
                title={darkMode ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}>
                {darkMode ? <Sun className="w-5 h-5 text-yellow-400" /> : <Moon className="w-5 h-5 text-gray-700" />}
              </button>
              <button
                onClick={() => setShowIndicatorsModal(true)}
                className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg transition-colors text-white">

                <Info className="w-4 h-4" />
                <span>Acerca de Indicadores de Priorización</span>
              </button>
              <button
                onClick={exportData}
                className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg transition-colors">

                <Download className="w-4 h-4" />
                <span>Exportar</span>
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Modal de Indicadores de Priorización */}
      {showIndicatorsModal &&
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-gray-800 rounded-xl p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto">

            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <Info className="w-6 h-6 text-green-400" />
                <h2 className="text-2xl font-bold">Indicadores de Priorización</h2>
              </div>
              <button
              onClick={() => setShowIndicatorsModal(false)}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors">

                <X className="w-6 h-6 text-gray-400 hover:text-white" />
              </button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-3 px-4 font-medium text-gray-300">Indicador</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-300">Ponderación</th>
                  </tr>
                </thead>
                <tbody>
                  {indicadoresPriorizacion.map((item, index) =>
                <tr
                  key={index}
                  className={`border-b border-gray-700/50 hover:bg-gray-700/30 ${
                  item.indicador === 'ÍNDICE DE PRIORIDAD TOTAL' ? 'bg-gray-700/20 font-semibold' : ''}`
                  }>

                      <td className="py-3 px-4">{item.indicador}</td>
                      <td className="py-3 px-4">
                        <span className={`${
                    item.indicador === 'ÍNDICE DE PRIORIDAD TOTAL' ? 'text-blue-300 font-semibold' : 'text-white'}`
                    }>
                          {item.ponderacion}
                        </span>
                      </td>
                    </tr>
                )}
                </tbody>
              </table>
            </div>
            
            <div className="mt-6 p-4 bg-gray-700/30 rounded-lg">
              <p className="text-sm text-gray-300">
                <strong>Nota:</strong> Los indicadores de priorización se utilizan para jerarquizar las microcuencas 
                según su importancia para la conservación y gestión del recurso hídrico. El índice de prioridad total 
                es la suma ponderada de todos los indicadores individuales.
              </p>
            </div>
          </motion.div>
        </div>
      }

      <div className="container mx-auto px-6 py-8">
        {/* Stats Cards - Datos Filtrados */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">

          {statsCards.map((stat, index) =>
          <div
            key={stat.title}
            className={`bg-gradient-to-r ${stat.color} p-6 rounded-xl shadow-lg`}>

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm">{stat.title}</p>
                  <p className="text-2xl font-bold text-white">{stat.value}</p>
                </div>
                <stat.icon className="w-8 h-8 text-white/80" />
              </div>
            </div>
          )}
        </motion.div>

        {/* Estadísticas Generales de Chiapas */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.05 }}
          className="mb-8">

          <h2 className={`text-xl font-bold mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-800'}`}>Estadísticas Generales de las microcuencas analizadas Chiapas</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {chiapasStatsCards.map((stat, index) =>
            <div
              key={stat.title}
              className={`bg-gradient-to-r ${stat.color} p-4 rounded-xl shadow-lg`}>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/80 text-xs">{stat.title}</p>
                    <p className="text-lg font-bold text-white">{stat.value}</p>
                  </div>
                  <stat.icon className="w-6 h-6 text-white/80" />
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Separador de Sección */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.15 }}
          className="mb-6">
          <div className="flex items-center">
            <div className={`flex-grow border-t ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}></div>
            <h2 className={`px-4 text-xl font-bold ${darkMode ? 'text-gray-300' : 'text-gray-800'}`}>Visualizaciones y Tablas de Valores de las Variables

            </h2>
            <div className={`flex-grow border-t ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}></div>
          </div>
        </motion.div>

        {/* Controls */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className={`rounded-xl p-6 mb-8 ${darkMode ? 'bg-gray-800' : 'bg-gray-50 border border-gray-200'}`}>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            {/* Analysis Mode */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Modo de Análisis
              </label>
              <select
                value={analysisMode}
                onChange={(e) => setAnalysisMode(e.target.value as any)}
                className={`w-full border rounded-lg px-3 py-2 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}>

                <option value="municipality">Por Municipio</option>
                <option value="microcuenca">Por Microcuenca</option>
                <option value="comparison">Comparación</option>
              </select>
            </div>

            {/* Municipality Filter */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Municipio
              </label>
              <select
                value={selectedMunicipality}
                onChange={(e) => setSelectedMunicipality(e.target.value)}
                className={`w-full border rounded-lg px-3 py-2 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}>

                <option value="all">Todos los Municipios</option>
                {municipalities.map((municipality) =>
                <option key={municipality} value={municipality}>
                    {municipality}
                  </option>
                )}
              </select>
            </div>

            {/* Microcuenca Filter */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Microcuenca
              </label>
              <select
                value={selectedMicrocuenca}
                onChange={(e) => setSelectedMicrocuenca(e.target.value)}
                className={`w-full border rounded-lg px-3 py-2 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}>

                <option value="all">Todas las Microcuencas</option>
                {microcuencasNames.map((microcuenca) =>
                <option key={microcuenca} value={microcuenca}>
                    {microcuenca}
                  </option>
                )}
              </select>
            </div>

            {/* Metric Selection */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Variable a Analizar
              </label>
              <select
                value={selectedMetric}
                onChange={(e) => setSelectedMetric(e.target.value)}
                className={`w-full border rounded-lg px-3 py-2 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}>

                {metrics.map((metric) =>
                <option key={metric.key} value={metric.key}>
                    {metric.label}
                  </option>
                )}
              </select>
            </div>

            {/* Chart Type */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Tipo de Gráfica
              </label>
              <select
                value={chartType}
                onChange={(e) => setChartType(e.target.value as any)}
                className={`w-full border rounded-lg px-3 py-2 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}>

                <option value="bar">Barras</option>
                <option value="line">Líneas</option>
                <option value="area">Área</option>
                <option value="pie">Circular</option>
                <option value="scatter">Dispersión</option>
              </select>
            </div>

            {/* Apply Filters Button */}
            <div className="flex items-end">
              <button
                onClick={fetchMicrocuencas}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2">

                <Filter className="w-4 h-4" />
                <span>Actualizar</span>
              </button>
            </div>
          </div>
        </motion.div>

        {/* Main Chart */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className={`rounded-xl p-6 mb-8 ${darkMode ? 'bg-gray-800' : 'bg-gray-50 border border-gray-200'}`}>

          <div className="flex items-center justify-between mb-6">
            <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Análisis {analysisMode === 'municipality' ? 'por Municipio' :
              analysisMode === 'microcuenca' ? 'por Microcuenca' : 'Comparativo'}
            </h2>
            <div className={`flex items-center space-x-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              <MapPin className="w-4 h-4" />
              <span>{filteredData.length} registros</span>
            </div>
          </div>
          
          {renderChart()}
        </motion.div>

        {/* Cuadro de Resumen Estadístico */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.35 }}
          className={`rounded-xl p-6 mb-8 border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>

          <div className="flex items-center space-x-3 mb-6">
            <FileText className={`w-6 h-6 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
            <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Resumen Estadístico</h2>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Información General */}
            





















            {/* Distribución de Uso de Suelo */}
            <div className="bg-gray-700/50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-green-300 mb-3">Uso de Suelo</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-300">Agrícola:</span>
                  <span className="font-medium">
                    {summaryData.distribucionUso.agricola.toLocaleString()} ha 
                    <span className="text-green-400 ml-1">
                      ({summaryData.distribucionUso.porcentajes.agricola.toFixed(1)}%)
                    </span>
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Pasto:</span>
                  <span className="font-medium">
                    {summaryData.distribucionUso.pasto.toLocaleString()} ha
                    <span className="text-yellow-400 ml-1">
                      ({summaryData.distribucionUso.porcentajes.pasto.toFixed(1)}%)
                    </span>
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Forestal:</span>
                  <span className="font-medium">
                    {summaryData.distribucionUso.forestal.toLocaleString()} ha
                    <span className="text-emerald-400 ml-1">
                      ({summaryData.distribucionUso.porcentajes.forestal.toFixed(1)}%)
                    </span>
                  </span>
                </div>
              </div>
            </div>

            {/* Estadísticas de la Métrica Seleccionada */}
            <div className="bg-gray-700/50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-purple-300 mb-3">
                {summaryData.metricaSeleccionada.nombre}
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-300">Promedio:</span>
                  <span className="font-medium">
                    {summaryData.metricaSeleccionada.promedio.toFixed(2)} {summaryData.metricaSeleccionada.unidad}
                  </span>
                </div>
                <div className="border-t border-gray-600 pt-2 mt-2">
                  <div className="flex justify-between items-start">
                    <span className="text-gray-300">Máximo:</span>
                    <div className="text-right">
                      <div className="font-medium text-red-400">
                        {summaryData.metricaSeleccionada.maximo.valor.toFixed(2)} {summaryData.metricaSeleccionada.unidad}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        {summaryData.metricaSeleccionada.maximo.ubicacion}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="border-t border-gray-600 pt-2">
                  <div className="flex justify-between items-start">
                    <span className="text-gray-300">Mínimo:</span>
                    <div className="text-right">
                      <div className="font-medium text-blue-400">
                        {summaryData.metricaSeleccionada.minimo.valor.toFixed(2)} {summaryData.metricaSeleccionada.unidad}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        {summaryData.metricaSeleccionada.minimo.ubicacion}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Data Table Preview */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className={`rounded-xl p-6 ${darkMode ? 'bg-gray-800' : 'bg-gray-50 border border-gray-200'}`}>

          <h2 className={`text-xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Vista Previa de Datos</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-300'}`}>
                  <th className={`text-left py-3 px-4 font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Municipio</th>
                  <th className={`text-left py-3 px-4 font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Microcuenca</th>
                  <th className={`text-left py-3 px-4 font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Superficie Agrícola</th>
                  <th className={`text-left py-3 px-4 font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Superficie Total</th>
                  <th className={`text-left py-3 px-4 font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Densidad Población</th>
                  <th className={`text-left py-3 px-4 font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Precipitación</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.slice(0, 10).map((item, index) =>
                <tr key={index} className={`border-b ${darkMode ? 'border-gray-700/50 hover:bg-gray-700/30' : 'border-gray-200 hover:bg-gray-100'}`}>
                    <td className={`py-3 px-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{item.municipio}</td>
                    <td className={`py-3 px-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{item.microcuenca}</td>
                    <td className={`py-3 px-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{item.superficie_agricola.toFixed(2)} ha</td>
                    <td className={`py-3 px-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{item.sup_micro.toFixed(2)} ha</td>
                    <td className={`py-3 px-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{item.densidad_poblacion.toFixed(2)} hab/km²</td>
                    <td className={`py-3 px-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{item.precipitacion.toFixed(2)} mm</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {filteredData.length > 10 &&
          <div className={`mt-4 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Mostrando 10 de {filteredData.length} registros
            </div>
          }
        </motion.div>
      </div>
    </div>);

};

export default InteractiveAnalysis;